import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, Card, Typography } from '@mui/material'
import React from 'react'
import { currencyFormatter } from 'src/Libs/shop/utils/formatter'
import { Product } from 'src/Libs/shop/models/power-meter-modal.model'
import { PowerMeterQuestionOptions, PowerMeterTypes } from 'src/Libs/shop/types'
import { makeStyles } from 'tss-react/mui'

interface PowerMeterOptionProps {
  productData: Product
  selectedProduct: string
  onPowerMeterChange: (newPowerMeterModel: PowerMeterQuestionOptions, powerMeterId: string) => void
}

const useStyles = makeStyles()(theme => ({
  optionContainer: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: theme.spacing(3),
  },
  options: {
    position: 'relative',
    paddingTop: theme.spacing(3),
    boxShadow: 'none',
    border: `1px solid ${theme.palette.grey.grey06}`,
    marginTop: theme.spacing(2),

    '&:hover': {
      cursor: 'pointer',
    },
  },
  optionsActive: {
    border: `1px solid ${theme.palette.primary.main}`,
  },
  selected: {
    position: 'absolute',
    top: theme.spacing(2),
    right: theme.spacing(2),
    backgroundColor: theme.palette.primary.main,
    padding: `${theme.spacing(0)} ${theme.spacing(2)}`,
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.primary.contrastText,
  },

  optionTextContainer: {
    display: 'flex',
    justifyContent: 'space-between',
  },
}))

const PowerMeterOption: React.FC<PowerMeterOptionProps> = ({ productData, selectedProduct, onPowerMeterChange }) => {
  const { t } = useCustomTranslation()
  const { classes, cx } = useStyles()
  const price = currencyFormatter(productData.calculatedPrice.unitPrice)

  return (
    <Box className={classes.optionContainer}>
      {productData.extensions.e3dcData.customFields.powerMeterType === PowerMeterTypes.WithEthernet && (
        <Card
          key={productData.id}
          className={cx(classes.options, selectedProduct === PowerMeterQuestionOptions.WithEthernet && classes.optionsActive)}
          onClick={() => {
            onPowerMeterChange(PowerMeterQuestionOptions.WithEthernet, productData.id)
          }}
        >
          {selectedProduct === PowerMeterQuestionOptions.WithEthernet && (
            <Typography className={classes.selected}>{t('shop.common.selected')}</Typography>
          )}
          <Typography variant="h3">{t('shop.power-meter.lm3pu-with-ethernet')}</Typography>
          <Box className={classes.optionTextContainer}>
            <Typography>{t('shop.power-meter.lm3pu-with-ethernet-description')}</Typography>
            <Typography>{price}</Typography>
          </Box>
        </Card>
      )}
      {productData.extensions.e3dcData.customFields.powerMeterType === PowerMeterTypes.WithoutEthernet && (
        <Card
          key={productData.id}
          className={cx(classes.options, selectedProduct === PowerMeterQuestionOptions.WithoutEthernet && classes.optionsActive)}
          onClick={() => {
            onPowerMeterChange(PowerMeterQuestionOptions.WithoutEthernet, productData.id)
          }}
        >
          {selectedProduct === PowerMeterQuestionOptions.WithoutEthernet && (
            <Typography className={classes.selected}>{t('shop.common.selected')}</Typography>
          )}
          <Typography variant="h3">{t('shop.power-meter.lm3pu-without-ethernet')}</Typography>
          <Box className={classes.optionTextContainer}>
            <Typography>{t('shop.power-meter.lm3pu-without-ethernet-description')}</Typography>

            <Typography>{price}</Typography>
          </Box>
        </Card>
      )}
      {productData.extensions.e3dcData.customFields.powerMeterType === PowerMeterTypes.Other && (
        <Card
          key={productData.id}
          className={cx(classes.options, selectedProduct === PowerMeterQuestionOptions.Other && classes.optionsActive)}
          onClick={() => {
            onPowerMeterChange(PowerMeterQuestionOptions.Other, productData.id)
          }}
        >
          {selectedProduct === PowerMeterQuestionOptions.Other && <Typography className={classes.selected}>{t('shop.common.selected')}</Typography>}
          <Typography variant="h3">{t('shop.power-meter.lm3p80hhc')}</Typography>
          <Box className={classes.optionTextContainer}>
            <Typography>{t('shop.power-meter.lm3p80hhc-description')}</Typography>

            <Typography>{price}</Typography>
          </Box>
        </Card>
      )}
    </Box>
  )
}

export default PowerMeterOption
